"use client";

import React, {FC, useCallback, useEffect, useRef, useState} from "react";
import {Button} from "@/shared/components/ui/button";
import {useUser} from "@/shared/lib/UserContext";
import {AlertCircle, CheckCircle} from "lucide-react";
import {OrderActionContent} from "@/features/components/StatusChangeModal/OrderActionContent";
import {ReservationActionContent} from "@/features/components/StatusChangeModal/ReservationActionContent";
import {
    CancelReservationHandler,
    changeStatusActionLabels,
    getAvailableActions,
    getNewStatusFromCode,
    OrderFormData,
    ReservationFormData,
    SaveOrderHandler,
    SaveReservationHandler,
    StatusCode,
} from "@/features/types/StatusChangeInterface";
import {cancelReservationAction, saveOrderAction, saveReservationAction} from "@/features/controllers/StatusChangeController";
import {getAutoTableOptions} from "@/features/controllers/AutoTableController";
import {CancelReservationActionContent} from "@/features/components/StatusChangeModal/CancelReservationActionContent";
import {Row} from "@tanstack/react-table";
import {AutoTableInterface} from "@/features/types/AutoTableInterface";

export const hasStatusHandler = (statusCode: string | null): boolean => {
    const code = statusCode || "";
    const availableActions = getAvailableActions(code);
    return availableActions.length > 0;
};

export type StatusChangeResult =
    | {
    type: 'order';
    ord_num: string | null;
    ord_user_name: string | null;
    ord_year: number | null;
    ord_pri_selected: number | null;
}
    | {
    type: 'reservation' | 'cancel';
    success: boolean;
};

export interface StatusChangeModalProps {
    row: Row<AutoTableInterface>;
    targetStatusCode: StatusCode; // Kod statusu docelowego (akcja)
    onConfirm?: (result?: StatusChangeResult) => void;
    onClose: () => void;
}

export const StatusChangeModal: FC<StatusChangeModalProps> = ({
                                                                  row,
                                                                  targetStatusCode,
                                                                  onConfirm,
                                                                  onClose,
                                                              }) => {
    const {userName} = useUser();
    const title = changeStatusActionLabels[targetStatusCode] || "Zmiana statusu";
    const modalRef = useRef<HTMLDivElement>(null);
    const [formData, setFormData] = useState<OrderFormData | ReservationFormData | null>(null);
    const [saving, setSaving] = useState(false);
    const [validation, setValidation] = useState<{ type: "error" | "success"; message: string } | null>(null);
    const [availableStatuses, setAvailableStatuses] = useState<string[]>([]);

    // Pobierz dostępne statusy z bazy przy montowaniu komponentu
    useEffect(() => {
        getAutoTableOptions("f_supp_2_status_desc", {})
            .then(setAvailableStatuses)
            .catch((err) => {
                console.error("Błąd podczas pobierania dostępnych statusów:", err);
            });
    }, []);

    // Wspólna funkcja do walidacji i pobierania nowego statusu
    const getValidatedNewStatus = (): string => {
        // Dla "" (pusty string) zwracamy pusty string (usuwamy rezerwację, status -> NULL)
        if (targetStatusCode === "") {
            return "";
        }
        const newStatus = getNewStatusFromCode(targetStatusCode, availableStatuses);
        if (!newStatus) {
            const actionLabel = changeStatusActionLabels[targetStatusCode] || "nieznana";
            throw new Error(`Nie można określić nowego statusu dla akcji "${actionLabel}"`);
        }
        return newStatus;
    };

    // Handler dla akcji zamówienia (targetStatusCode "4")
    const handleOrderAction: SaveOrderHandler = async ({row, formData, userName}) => {
        const orderFormData = formData as OrderFormData;
        if (!orderFormData.client?.f_crm_id || !orderFormData.ord_vehicle_type) {
            throw new Error("Brak wymaganych danych");
        }
        // userName jest zawsze dostępne w produkcji (sprawdzane w UserContext)
        if (!userName) {
            throw new Error("Brak nazwy użytkownika");
        }

        const newStatus = getValidatedNewStatus();

        const saveData = {
            f_id: row.original.f_id,
            f_loc_lit: row.original.f_loc_lit,
            f_ord_vehicle_type: orderFormData.ord_vehicle_type,
            f_ord_client_id: orderFormData.client.f_crm_id || "",
            f_ord_user_name: userName,
            f_ord_status_desc: newStatus,
            f_ord_sales_agr_issue_dat: orderFormData.ord_sales_agr_issue_dat
                ? orderFormData.ord_sales_agr_issue_dat.toISOString().split('T')[0]
                : "",
            f_ord_sales_agr_registr_dat: orderFormData.ord_sales_agr_registr_dat
                ? orderFormData.ord_sales_agr_registr_dat.toISOString().split('T')[0]
                : "",
            f_ord_sales_price: orderFormData.ord_sales_price,
        };

        return await saveOrderAction(saveData);
    };

    // Handler dla akcji rezerwacji (targetStatusCode "5")
    const handleReservationAction: SaveReservationHandler = async ({row, formData}) => {
        const reservationFormData = formData as ReservationFormData;

        if (!reservationFormData.f_sales_user_name) {
            throw new Error("Proszę wybrać użytkownika");
        }

        if (!reservationFormData.reservation_type) {
            throw new Error("Proszę wybrać typ rezerwacji");
        }

        // Oblicz datę czynności: dzisiaj + reservation_days + 1 dzień (bo rezerwacja trwa do końca dnia)
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const actionDate = new Date(today);
        // Dodajemy reservation_days + 1, bo np. dla 4 stycznia + 3 dni powinno być 7 stycznia (nie 6)
        actionDate.setDate(actionDate.getDate() + (reservationFormData.reservation_days ?? 0) + 1);
        const actionDateStr = actionDate.toISOString().split('T')[0];

        const saveData = {
            f_id: row.original.f_id,
            f_loc_lit: row.original.f_loc_lit,
            f_ord_num: row.original.f_ord_num,
            f_ord_d: row.original.f_ord_d,
            f_sales_user_name: reservationFormData.f_sales_user_name,
            reservation_days: reservationFormData.reservation_days ?? 0,
            reservation_type: reservationFormData.reservation_type,
            action_date: actionDateStr,
            // new_status nie jest potrzebne - backend obliczy "5 - Zarezerw."
        };

        return await saveReservationAction(saveData);
    };

    // Handler dla anulowania rezerwacji (targetStatusCode "")
    const handleCancelReservationAction: CancelReservationHandler = async ({row}) => {
        if (!row.original.f_ord_num) {
            throw new Error("Brak numeru zamówienia");
        }

        const cancelData = {
            f_id: row.original.f_id,
            f_loc_lit: row.original.f_loc_lit,
            f_ord_num: row.original.f_ord_num,
            f_ord_d: row.original.f_ord_d,
        };

        return await cancelReservationAction(cancelData);
    };

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
                onClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    // Walidacja formularza - tylko sprawdzenia związane z danymi formularza
    const handleValidation = (): boolean => {
        setValidation(null);

        // Dla "" (anulowanie rezerwacji) nie wymagamy formData
        if (targetStatusCode !== "" && !formData) {
            setValidation({
                type: "error",
                message: "Brak danych formularza. Uzupełnij wymagane pola.",
            });
            return false;
        }

        // Walidacja specyficzna dla akcji zamówienia (targetStatusCode "4")
        if (targetStatusCode === "4") {
            const orderFormData = formData as OrderFormData;

            if (!orderFormData.client || !orderFormData.client.f_crm_id) {
                setValidation({
                    type: "error",
                    message: "Proszę wybrać klienta (ID klienta jest wymagane).",
                });
                return false;
            }

            if (!orderFormData.ord_vehicle_type) {
                setValidation({
                    type: "error",
                    message: "Proszę wybrać typ pojazdu (Osobowy lub Ciężarowy).",
                });
                return false;
            }

            if (
                !orderFormData.ord_sales_agr_issue_dat ||
                !orderFormData.ord_sales_agr_registr_dat ||
                !orderFormData.ord_sales_price
            ) {
                setValidation({
                    type: "error",
                    message: "Proszę uzupełnić daty oraz prognozowaną marżę netto.",
                });
                return false;
            }

            // Walidacja dat - muszą być co najmniej jutro
            const validateFutureDate = (date: Date | null): boolean => {
                if (!date) return false;
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const selectedDate = new Date(date);
                selectedDate.setHours(0, 0, 0, 0);
                const tomorrow = new Date(today);
                tomorrow.setDate(tomorrow.getDate() + 1);

                return selectedDate >= tomorrow;
            };

            if (!validateFutureDate(orderFormData.ord_sales_agr_issue_dat) || !validateFutureDate(orderFormData.ord_sales_agr_registr_dat)) {
                setValidation({
                    type: "error",
                    message: "Daty są nieprawidłowe.",
                });
                return false;
            }
        }

        // Walidacja specyficzna dla akcji rezerwacji (targetStatusCode "5")
        if (targetStatusCode === "5") {
            const reservationFormData = formData as ReservationFormData;

            if (!reservationFormData.f_sales_user_name) {
                setValidation({
                    type: "error",
                    message: "Proszę wybrać użytkownika.",
                });
                return false;
            }

            if (!reservationFormData.reservation_type) {
                setValidation({
                    type: "error",
                    message: "Proszę wybrać typ rezerwacji.",
                });
                return false;
            }

            if (reservationFormData.reservation_type === "short" && (reservationFormData.reservation_days === null || reservationFormData.reservation_days <= 0)) {
                setValidation({
                    type: "error",
                    message: "Dla rezerwacji krótkoterminowej liczba dni musi być większa od 0.",
                });
                return false;
            }
        }

        return true;
    };

    const handleSave = async () => {
        // Walidacja formularza
        if (!handleValidation()) {
            return;
        }

        // userName jest zawsze dostępne w produkcji (sprawdzane w UserContext)
        if (!userName) {
            console.error("userName nie jest dostępne - to nie powinno się zdarzyć w produkcji");
            return;
        }

        setSaving(true);
        try {
            let result: StatusChangeResult;

            if (targetStatusCode === "4") {
                // Zamówienie - zwraca pełne dane
                const orderResult = await handleOrderAction({row, formData: formData as OrderFormData, userName});
                result = {
                    type: 'order' as const,
                    ord_num: orderResult.ord_num,
                    ord_user_name: orderResult.ord_user_name,
                    ord_year: orderResult.ord_year,
                    ord_pri_selected: orderResult.ord_pri_selected,
                };
            } else if (targetStatusCode === "5") {
                // Rezerwacja - zwraca tylko success
                const reservationResult = await handleReservationAction({row, formData: formData as ReservationFormData, userName});
                result = { type: 'reservation' as const, success: reservationResult.success };
            } else if (targetStatusCode === "") {
                // Anulowanie rezerwacji - zwraca tylko success
                const cancelResult = await handleCancelReservationAction({row, userName});
                result = { type: 'cancel' as const, success: cancelResult.success };
            } else {
                setValidation({
                    type: "error",
                    message: "Ta akcja nie ma jeszcze zaimplementowanego zapisu.",
                });
                setSaving(false);
                return;
            }

            onConfirm?.(result);
            onClose();
        } catch (error) {
            console.error("Błąd podczas zapisywania:", error);
            setValidation({
                type: "error",
                message: "Wystąpił błąd podczas zapisywania danych. Spróbuj ponownie.",
            });
        } finally {
            setSaving(false);
        }
    };

    // Użyj useCallback aby uniknąć "Maximum update depth exceeded"
    const handleOrderDataChange = useCallback((data: OrderFormData) => {
        setFormData(data);
    }, []);

    const handleReservationDataChange = useCallback((data: ReservationFormData) => {
        setFormData(data);
    }, []);

    const handleCancelReservationConfirm = async () => {
        // userName jest zawsze dostępne w produkcji (sprawdzane w UserContext)
        if (!userName) {
            console.error("userName nie jest dostępne - to nie powinno się zdarzyć w produkcji");
            return;
        }

        setSaving(true);
        try {
            const result = await handleCancelReservationAction({row, userName});
            console.log('CancelReservation: Wywołuję onConfirm z result:', result);
            onConfirm?.({ type: 'cancel' as const, success: result.success });
            console.log('CancelReservation: onConfirm wywołane, teraz wywołuję onClose');
            onClose();
        } catch (error) {
            console.error("Błąd podczas anulowania rezerwacji:", error);
            setValidation({
                type: "error",
                message: "Wystąpił błąd podczas anulowania rezerwacji. Spróbuj ponownie.",
            });
        } finally {
            setSaving(false);
        }
    };

    const renderContent = () => {
        // Renderuj odpowiedni komponent w zależności od targetStatusCode
        if (targetStatusCode === "4") {
            // Zamówienie
            return <OrderActionContent row={row} onDataChange={handleOrderDataChange}/>;
        } else if (targetStatusCode === "5") {
            // Rezerwacja
            return <ReservationActionContent row={row} onDataChange={handleReservationDataChange}/>;
        } else if (targetStatusCode === "") {
            // Anulowanie rezerwacji - zwracamy AlertPopup bez modalu
            return (
                <CancelReservationActionContent
                    row={row}
                    onConfirm={handleCancelReservationConfirm}
                    onClose={onClose}
                />
            );
        }
        // Dla nieobsługiwanych akcji zwracamy null
        return null;
    }

    const content = renderContent();

    // Jeśli nie ma zawartości, nie renderuj modalu
    if (!content) {
        return null;
    }

    // Jeśli content to CancelReservationActionContent (AlertPopup), zwróć tylko ten komponent bez modalu
    if (targetStatusCode === "") {
        return content;
    }

    // Dla pozostałych akcji renderuj modal z zawartością
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 md:p-4">
            <div ref={modalRef} className="bg-white p-4 md:p-6 rounded-lg shadow-lg w-[95vw] max-w-[600px] max-h-[95vh] flex flex-col">
                <h2 className="text-lg md:text-xl font-semibold mb-4 md:mb-6 flex-shrink-0">{title}</h2>

                <div className="flex-1 overflow-x-hidden overflow-y-auto min-h-0">
                    {content}
                </div>

                {/* Walidacja i przyciski - zawsze na dole */}
                <div className="flex items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-600 flex-shrink-0">
                    {/* Walidacja po lewej */}
                    <div className="flex-1">
                        {validation && (
                            <div
                                className={`flex items-start gap-2 rounded-md border px-3 py-2 text-sm ${
                                    validation.type === "error"
                                        ? "border-red-200 bg-red-50 text-red-700"
                                        : "border-green-200 bg-green-50 text-green-700"
                                }`}
                            >
                                {validation.type === "error" ? (
                                    <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0"/>
                                ) : (
                                    <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0"/>
                                )}
                                <span>{validation.message}</span>
                            </div>
                        )}
                    </div>

                    {/* Przyciski po prawej */}
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={onClose} disabled={saving}>Anuluj</Button>
                        <Button onClick={handleSave} disabled={saving}>
                            {saving ? "Zapisywanie..." : "Zapisz"}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

